"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Mail, Phone, MapPin, Globe } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FOOTER_LINKS } from "@/lib/content";
import { getCompanyProfile } from "@/api/company-profile";
import { useScrollTriggerRefresh } from "@/lib/use-scroll-trigger-refresh";
import { Logo } from "./logo";
import { Container } from "./container";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

const SOCIAL_LABELS: Record<string, string> = {
  facebook: "Facebook",
  twitter: "Twitter",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  youtube: "YouTube",
};

export function Footer() {
  const bandRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["company-profile"],
    queryFn: getCompanyProfile,
    staleTime: 5 * 60 * 1000,
  });

  const companyName = data?.companyName;
  const mission = data?.mission;
  const address = data?.address;
  const email = data?.email;
  const phone = data?.phone;
  const socialLinks = data?.socialLinks
    ? Object.entries(data.socialLinks)
        .filter(([, href]) => Boolean(href))
        .map(([key, href]) => ({ href, label: SOCIAL_LABELS[key] ?? key }))
    : [];

  useEffect(() => {
    const root = bandRef.current;
    if (!root) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      // One-shot play-on-enter, triggered off the content wrapper rather than the outer band —
      // a scrub tied to the band's own top edge can finish before the content (padded py-[12vh])
      // actually scrolls into view, so the text appears pre-revealed with no visible motion.
      gsap.fromTo(
        "[data-cta-content] [data-meta]",
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.15,
          ease: "power2.out",
          duration: 1.4,
          scrollTrigger: { trigger: "[data-cta-content]", start: "top 90%", toggleActions: "play none none reverse" },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  useScrollTriggerRefresh([]);

  return (
    <footer className="relative overflow-hidden border-t border-[var(--color-border)] bg-[var(--color-bg)]">
      {/* oversized closing statement — the dominant visual moment of the footer */}
      <div ref={bandRef} className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(65% 70% at 50% 100%, color-mix(in oklch, var(--color-primary) 26%, transparent) 0%, transparent 72%)" }}
        />
        {/* faint grid texture, masked to fade toward the edges so it reads as atmosphere, not a pattern */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "linear-gradient(color-mix(in oklch, var(--color-border) 80%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklch, var(--color-border) 80%, transparent) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(70% 90% at 50% 60%, black, transparent)",
          }}
        />
        <Container data-cta-content className="group/band relative z-[var(--z-content,10)] pt-20 pb-14 sm:pt-24">
          <p data-meta className="text-sm text-[var(--color-text-tertiary)]">Got an idea worth building?</p>
          <Link
            href="/contact"
            data-meta
            className="group relative mt-3 block w-fit font-display text-[clamp(3rem,10vw,8.5rem)] font-bold leading-[0.88] tracking-[-0.04em] text-[var(--color-text-primary)] text-balance transition-colors duration-300 hover:text-[var(--color-primary-text)]"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-x-8 -inset-y-6 -z-10 rounded-[3rem] opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
              style={{ background: "radial-gradient(closest-side, color-mix(in oklch, var(--color-primary) 35%, transparent), transparent)" }}
            />
            Let&apos;s talk.
            <ArrowUpRight
              aria-hidden
              className="ml-2 inline h-[0.62em] w-[0.62em] -translate-y-[0.06em] opacity-50 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-[0.18em] group-hover:opacity-100"
            />
          </Link>
        </Container>
      </div>

      {/* practical footer: one relaxed row — brand statement on the left, everything else as
          loose inline groups on the right, instead of four boxed columns */}
      <Container className="relative z-[var(--z-content,10)] border-t border-[var(--color-border)] py-12">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
          <div className="max-w-sm">
            <Link href="/" className="flex items-center" aria-label={companyName ? `${companyName} home` : "Home"}>
              <Logo className="h-7" />
            </Link>
            {isLoading ? (
              <div className="mt-4 max-w-xs space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-[var(--color-bg-elevated)]" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
              </div>
            ) : (
              mission && (
                <p className="mt-4 text-sm leading-relaxed text-[var(--color-text-tertiary)]">{mission}</p>
              )
            )}
          </div>

          <div className="flex flex-col gap-8 sm:flex-row sm:flex-wrap sm:gap-x-14 sm:gap-y-8 lg:justify-end">
            <nav aria-label="Footer" className="flex flex-wrap gap-x-6 gap-y-2.5">
              {FOOTER_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-primary)]"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2.5">
              {isLoading ? (
                <>
                  <div className="h-3 w-32 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
                  <div className="h-3 w-24 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
                </>
              ) : (
                <>
                  {email && (
                    <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-primary)]">
                      <Mail size={13} className="shrink-0 text-[var(--color-text-tertiary)]" aria-hidden />
                      {email}
                    </a>
                  )}
                  {phone && (
                    <a href={`tel:${phone}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-primary)]">
                      <Phone size={13} className="shrink-0 text-[var(--color-text-tertiary)]" aria-hidden />
                      {phone}
                    </a>
                  )}
                  {address && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-tertiary)]">
                      <MapPin size={13} className="shrink-0" aria-hidden />
                      {address}
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
              {isLoading ? (
                <div className="h-3 w-28 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
              ) : socialLinks.length > 0 ? (
                socialLinks.map((social) => (
                  <a
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-primary)]"
                  >
                    {social.label}
                  </a>
                ))
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-tertiary)]">
                  <Globe size={13} aria-hidden />
                  exec9.com
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col-reverse items-center gap-3 border-t border-[var(--color-border)] pt-6 sm:flex-row sm:justify-between">
          <p className="text-xs text-[var(--color-text-tertiary)]">&copy; {new Date().getFullYear()} {companyName ?? "Exec9"}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/privacy-policy" className="text-xs text-[var(--color-text-tertiary)] transition-colors hover:text-[var(--color-primary)]">Privacy Policy</Link>
            <Link href="/terms-and-conditions" className="text-xs text-[var(--color-text-tertiary)] transition-colors hover:text-[var(--color-primary)]">Terms &amp; Conditions</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}
